import { useCallback, useEffect, useReducer, useRef } from 'react'
import type { UseFormMethods } from 'react-hook-form'
import { DefaultValues, FieldValues, FormState, SubmitHandler } from 'react-hook-form'

interface SubmitReducer {
  state: 'initial' | 'invalid' | 'validating' | 'validated' | 'submitting' | 'submitted'
}

type SubmitReducerAction = {
  type: 'formStateUpdated'
  formState: Pick<
    FormState<unknown>,
    'isDirty' | 'isSubmitted' | 'isSubmitting' | 'isSubmitSuccessful' | 'isValid' | 'isValidating'
  >
}

function submitReducerCallback(
  prevValue: SubmitReducer,
  action: SubmitReducerAction,
): SubmitReducer {
  const { state: prevState } = prevValue
  if (action.type === 'formStateUpdated') {
    const { formState } = action
    if (prevState === 'initial') {
      return formState.isValidating ? { ...prevValue, state: 'validating' } : prevValue
    }
    if (prevState === 'invalid') {
      if (formState.isValidating) {
        return { ...prevValue, state: 'validating' }
      }
      // handle scenario where form becomes valid immediately after input is unregistered
      return formState.isDirty && formState.isValid
        ? { ...prevValue, state: 'validated' }
        : prevValue
    }
    if (prevState === 'validating') {
      if (formState.isValidating) {
        return prevValue
      }
      return formState.isDirty && formState.isValid
        ? { ...prevValue, state: 'validated' }
        : { ...prevValue, state: 'invalid' }
    }
    if (prevState === 'validated') {
      return formState.isSubmitting ? { ...prevValue, state: 'submitting' } : prevValue
    }
    if (prevState === 'submitting') {
      if (formState.isSubmitting) {
        return prevValue
      }
      if (formState.isSubmitSuccessful) {
        return { ...prevValue, state: 'submitted' }
      }
      return { ...prevValue, state: 'initial' }
    }
    if (prevState === 'submitted') {
      return !formState.isSubmitted ? { ...prevValue, state: 'initial' } : prevValue
    }
  }
  return prevValue
}

export interface UseSubmitFormOnDirtyProps<TFieldValues extends FieldValues>
  extends Pick<UseFormMethods<TFieldValues>, 'formState' | 'handleSubmit' | 'reset'> {
  /** Must be memoized/cached */
  defaultValues: DefaultValues<TFieldValues>
  onSubmit: SubmitHandler<TFieldValues>
  /** Set to false when automatic submit not desired (e.g. confirmation needed) */
  submitOnDirty?: boolean
}

export interface UseSubmitFormOnDirty {
  // useForm().reset wrapper bound to current default values
  resetForm(): void
}

const useSubmitFormOnDirty = <TFieldValues extends FieldValues>({
  defaultValues,
  formState,
  handleSubmit,
  onSubmit,
  reset,
  submitOnDirty = true,
}: UseSubmitFormOnDirtyProps<TFieldValues>): UseSubmitFormOnDirty => {
  const [{ state }, dispatch] = useReducer(submitReducerCallback, { state: 'initial' })
  const isInitialRenderRef = useRef(true)

  const resetForm = useCallback(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  useEffect(() => {
    dispatch({
      type: 'formStateUpdated',
      formState: {
        isDirty: formState.isDirty,
        isSubmitted: formState.isSubmitted,
        isSubmitting: formState.isSubmitting,
        isSubmitSuccessful: formState.isSubmitSuccessful,
        isValidating: formState.isValidating,
        isValid: formState.isValid,
      },
    })
  }, [
    formState.isDirty,
    formState.isSubmitSuccessful,
    formState.isSubmitted,
    formState.isSubmitting,
    formState.isValid,
    formState.isValidating,
  ])

  // submit changes automatically when state === 'validated'
  useEffect(() => {
    if (state !== 'validated' || !submitOnDirty) {
      return
    }

    handleSubmit(onSubmit)()
  }, [handleSubmit, onSubmit, state, submitOnDirty])

  // reset form when defaultValues change (e.g. after successful submit, external change)
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false
      return
    }
    reset(defaultValues)
  }, [defaultValues, reset])

  return { resetForm }
}

export default useSubmitFormOnDirty
